/**
 * Storage Abstraction Layer
 *
 * Provides a platform-agnostic storage interface for web and mobile.
 * Web uses localStorage, mobile will use AsyncStorage/SecureStore.
 *
 * This abstraction enables code sharing between web and React Native
 * while maintaining platform-specific optimizations.
 */

import { logger } from '../logger';

/**
 * Storage adapter interface
 * All methods are async to support both web (sync) and mobile (async) storage
 */
export interface StorageAdapter {
  /**
   * Store a value
   * @param key - Storage key
   * @param value - String value to store
   */
  setItem(key: string, value: string): Promise<void>;

  /**
   * Retrieve a value
   * @param key - Storage key
   * @returns Stored value or null if not found
   */
  getItem(key: string): Promise<string | null>;

  /**
   * Remove a value
   * @param key - Storage key
   */
  removeItem(key: string): Promise<void>;

  /**
   * Clear all storage
   * Use with caution!
   */
  clear(): Promise<void>;

  /**
   * Get all keys
   * @returns Array of all storage keys
   */
  getAllKeys(): Promise<string[]>;
}

/**
 * Secure storage adapter interface for sensitive data (tokens, credentials)
 * Uses localStorage on web, Keychain (iOS) or Keystore (Android) on mobile
 */
export interface SecureStorageAdapter extends StorageAdapter {
  /**
   * Check if secure storage is available
   */
  isAvailable(): Promise<boolean>;
}

/**
 * Storage errors
 */
export class StorageError extends Error {
  public readonly code: string;

  constructor(message: string, code: string) {
    super(message);
    this.name = 'StorageError';
    this.code = code;
  }
}

/**
 * Get the appropriate storage adapter for the current platform
 */
export async function getStorageAdapter(): Promise<StorageAdapter> {
  // Dynamically import based on platform
  // For web, this will always use web storage
  // For React Native, we'll add platform detection later

  if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
    // Web platform
    const { WebStorage } = await import('./web');
    return new WebStorage();
  } else {
    // Native platform (React Native)
    logger.warn('Storage', 'Native storage not yet implemented, falling back to web storage');
    const { WebStorage } = await import('./web');
    return new WebStorage();
  }
}

/**
 * Get secure storage adapter for sensitive data
 */
export async function getSecureStorageAdapter(): Promise<SecureStorageAdapter> {
  // For now, use the same adapter
  // On React Native, this will use Keychain/Keystore

  if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
    // Web platform - uses localStorage (encrypted at app level)
    const { WebSecureStorage } = await import('./web');
    return new WebSecureStorage();
  } else {
    // Native platform
    logger.warn('Storage', 'Native secure storage not yet implemented');
    const { WebSecureStorage } = await import('./web');
    return new WebSecureStorage();
  }
}

/**
 * Singleton storage instance
 * Lazy-initialized on first use
 */
let storageInstance: StorageAdapter | null = null;
let secureStorageInstance: SecureStorageAdapter | null = null;

/**
 * Get the singleton storage instance
 */
export async function getStorage(): Promise<StorageAdapter> {
  if (!storageInstance) {
    storageInstance = await getStorageAdapter();
    logger.debug('Storage', 'Storage adapter initialized');
  }
  return storageInstance;
}

/**
 * Get the singleton secure storage instance
 */
export async function getSecureStorage(): Promise<SecureStorageAdapter> {
  if (!secureStorageInstance) {
    secureStorageInstance = await getSecureStorageAdapter();
    logger.debug('Storage', 'Secure storage adapter initialized');
  }
  return secureStorageInstance;
}

/**
 * Reset storage instances (for testing)
 */
export function resetStorageInstances(): void {
  storageInstance = null;
  secureStorageInstance = null;
}
